import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { Stars } from "@/components/RateDelivery";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";

export const RatingsCard = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/ratings/summary").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  const max = Math.max(1, ...Object.values(d.distribution));
  return (
    <div className="rounded-2xl border border-[#E7E2D6] bg-white p-5 shadow-sm lg:col-span-4" data-testid="ratings-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-display text-lg font-semibold text-[#0F382C]"><Star className="h-5 w-5 fill-[#F2B880] text-[#E0A060]" />Customer ratings</div>
        <div className="text-xs text-muted-foreground">This week · since {fmtDate(d.since)}</div>
      </div>
      <div className="mt-4 grid gap-6 md:grid-cols-3">
        <div>
          <div className="font-mono text-4xl font-semibold" data-testid="ratings-average">{d.average ?? "-"}<span className="text-lg text-muted-foreground">/5</span></div>
          <div className="text-xs text-muted-foreground" data-testid="ratings-count">{d.count} ratings this week</div>
          <div className="mt-3 space-y-1">
            {Object.entries(d.distribution).map(([s, n]) => (
              <div key={s} className="flex items-center gap-2 text-xs" data-testid={`ratings-dist-${s}`}>
                <span className="w-6 font-mono">{s}★</span>
                <div className="h-2 flex-1 rounded-full bg-[#FAF4E8]"><div className="h-2 rounded-full bg-[#F2B880]" style={{ width: `${(n / max) * 100}%` }} /></div>
                <span className="w-6 text-right font-mono">{n}</span>
              </div>
            ))}
          </div>
          {d.top_tags.length > 0 && <div className="mt-3 flex flex-wrap gap-1" data-testid="ratings-top-tags">{d.top_tags.map((t) => <span key={t.tag} className="rounded-full bg-[#FAF4E8] px-2 py-0.5 text-[11px] font-semibold">{t.tag} · {t.count}</span>)}</div>}
        </div>
        <div className="md:col-span-2">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Recent low ratings (2★ or below, 30 days)</div>
          {d.problem_batches.length > 0 && <div className="mt-2 rounded-xl border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700" data-testid="ratings-problem-batches">Check batches: {d.problem_batches.map((b) => `${b.code} (${b.low_ratings} low)`).join(", ")}</div>}
          <div className="mt-2 space-y-2">
            {d.recent_low.length === 0 && <div className="text-sm text-muted-foreground">No low ratings. Great work!</div>}
            {d.recent_low.map((r) => (
              <Link key={r.order_id} to={`/customers/${r.customer_id}`} className="block rounded-xl bg-[#FBF9F4] p-3 text-sm hover:bg-[#FAF4E8]" data-testid={`low-rating-${r.order_id}`}>
                <div className="flex items-center justify-between"><span className="font-semibold">{r.customer_name}</span><Stars value={r.stars} size="h-3.5 w-3.5" /></div>
                <div className="text-xs text-muted-foreground">{fmtDate(r.date)} · {r.items}{r.batches.length ? ` · Batch ${r.batches.join(", ")}` : ""}</div>
                {(r.tags.length > 0 || r.comment) && <div className="mt-1 text-xs">{[...r.tags, r.comment].filter(Boolean).join(" · ")}</div>}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
